import React from "react";
import { useTranslation } from "react-i18next";

import { SUPPORT_WHATSAPP_NUMBER } from "../../setup/constants";
import styles from "./styles.module.scss";
import { BankCardIcon, ClockIcon, HomeIcon, ReviewDocIcon } from "./gateIcons";
import OrganizationReviewCard from "./OrganizationReviewCard";

/** A document row on the pending screen, with its label already resolved. */
export interface IReviewDocument {
  key: string;
  title: string;
  fileName?: string;
  /** Defaults to the document glyph. */
  icon?: React.ReactNode;
}

/** One-line summaries of the activation step's bank account and address. */
export interface IReviewDetails {
  /** e.g. "SA44 8000 0000 0123 4567 8912" */
  bank?: string;
  /** e.g. "RRRD1234 · الرياض، الملقا" */
  address?: string;
}

interface Props {
  /**
   * Documents on file, listed back as confirmation. Sourced either from this session's upload
   * or from the attachments endpoint on a later visit.
   */
  documents?: IReviewDocument[];
  /** From this session's submission, or from my-organization on a later visit. */
  details?: IReviewDetails;
}

const OrganizationPending: React.FC<Props> = ({ documents, details }) => {
  const { t } = useTranslation();

  // Bank + address lead the list, as in the design; all are under review together.
  const rows: IReviewDocument[] = [
    ...(details?.bank
      ? [
          {
            key: "bank",
            title: t("lbl_org_act_bank_title") as string,
            fileName: details.bank,
            icon: <BankCardIcon />,
          },
        ]
      : []),
    ...(details?.address
      ? [
          {
            key: "address",
            title: t("lbl_org_act_addr_title") as string,
            fileName: details.address,
            icon: <HomeIcon />,
          },
        ]
      : []),
    ...(documents ?? []),
  ];

  return (
    <OrganizationReviewCard
      variant="pending"
      title={t("lbl_org_pending_title")}
      description={t("lbl_org_pending_subtitle")}>
      {!!rows.length && (
        <div className={styles.docsReadOnly}>
          {rows.map(({ key, title, fileName, icon }) => (
            <div className={styles.dcRow} key={key}>
              <span className={styles.dcIcon}>{icon ?? <ReviewDocIcon />}</span>
              <span className={styles.dcTxt}>
                <b>{title}</b>
                {fileName && (
                  // title shows the full value when a long address ellipsizes.
                  <span className={styles.dcFile} dir="auto" title={fileName}>
                    {fileName}
                  </span>
                )}
              </span>
              <span className={styles.dcChip}>
                {t("lbl_org_docs_review_chip")}
              </span>
            </div>
          ))}
        </div>
      )}
      <div className={styles.reviewSep} />
      <div className={styles.reviewFoot}>
        <ClockIcon />
        {t("lbl_org_pending_duration")}
        {SUPPORT_WHATSAPP_NUMBER && (
          <>
            <span className={styles.dotSep}>·</span>
            <a
              className={styles.reviewFootLink}
              href={`https://wa.me/${SUPPORT_WHATSAPP_NUMBER}`}
              target="_blank"
              rel="noreferrer">
              {t("lbl_customer_service")}
            </a>
          </>
        )}
      </div>
    </OrganizationReviewCard>
  );
};

export default OrganizationPending;
